import React, { useEffect, useState, useCallback } from 'react'
import { useNavigate } from 'react-router-dom'
import { ArrowLeft, Bell, BellOff, CheckCircle, X } from 'lucide-react'
import { toast } from 'react-toastify'
import axios from 'axios'
import { useWorker } from '../context/WorkerContext'

const BASE_URL = process.env.REACT_APP_BASE_URL;

// ─── helpers ───────────────────────────────────────────────────────────────

function mapNotification(n) {
  return {
    id: n._id,
    title: n.notificationTitle || undefined,
    body: n.notificationBody || n.notificationMessage || '',
    button1Label: n.notificationButton1 || 'Yes',
    button2Label: n.notificationButton2 || 'No',
    has_actions: true,
    status: n.MessageStatus,
    canAct: n.MessageStatus === 'sent',
    responded: Boolean(n.MessageResponse),
    response: n.MessageResponse || undefined,
    created_at: n.created_at,
    read: n.MessageStatus !== 'sent',
  }
}

function NotificationCard({ notification, onOpen }) {
  const { title, body, has_actions, responded, response, read, canAct } = notification
  const hasTitle = Boolean(title)
  const interactive = canAct && !responded

  return (
    <div
      onClick={() => { if (interactive) onOpen(notification) }}
      className={`bg-white rounded-xl mx-4 mb-3 shadow-sm border overflow-hidden transition-all
        ${interactive ? 'border-indigo-200 cursor-pointer hover:shadow-md active:scale-[0.99]' : 'border-gray-100'}`}
    >
      {interactive && <div className="h-0.5 bg-indigo-400 w-full" />}

      <div className="p-4">
        <div className="flex items-start justify-between gap-2 mb-1">
          {hasTitle ? (
            <p className={`text-sm font-semibold leading-snug flex-1
              ${interactive ? 'text-indigo-700' : 'text-gray-700'}`}>
              {title}
            </p>
          ) : (
            <span className={`mt-1 w-1.5 h-1.5 rounded-full flex-shrink-0
              ${interactive ? 'bg-indigo-400' : 'bg-gray-300'}`} />
          )}
        </div>

        <p className={`text-sm leading-relaxed ${hasTitle ? 'text-gray-600 mt-0.5' : 'text-gray-700'}`}>
          {body}
        </p>

        {has_actions && (
          <div className="mt-3">
            {responded ? (
              <div className="flex items-center gap-1.5 text-xs text-gray-400">
                <CheckCircle className="w-3.5 h-3.5 text-green-400" />
                <span>
                  You responded <span className="font-medium text-gray-500 capitalize">{response}</span>
                </span>
              </div>
            ) : interactive ? (
              <p className="text-xs text-indigo-500 font-medium">Tap to respond →</p>
            ) : (
              <p className="text-xs text-gray-400">No action available</p>
            )}
          </div>
        )}
      </div>
    </div>
  )
}

const NotificationHistoryPage = () => {
  const navigate = useNavigate()
  const { user, token, submitMessageResponse, markMessageAsRead } = useWorker()
  const [notifications, setNotifications] = useState([])
  const [loading, setLoading] = useState(true)
  const [showMessageModal, setShowMessageModal] = useState(false)
  const [currentMessage, setCurrentMessage] = useState(null)
  const [actionLoading, setActionLoading] = useState(false)

  const fetchNotifications = useCallback(async () => {
    if (!user?.id) return
    setLoading(true)
    try {
      const response = await axios.post(
        `${BASE_URL}/lenskart-admin/notification-history`,
        { userId: user.id, limit: 10 },
        { headers: { Authorization: `Bearer ${token}` } }
      )

      const raw = response.data?.notifications ?? response.data ?? []
      setNotifications(raw.map(mapNotification))
    } catch (err) {
      toast.error('Could not load notifications')
    } finally {
      setLoading(false)
    }
  }, [user?.id, token])

  useEffect(() => {
    fetchNotifications()
  }, [fetchNotifications])

  const handleOpenNotification = (notification) => {
    setCurrentMessage(notification)
    setShowMessageModal(true)
  }

  const handleMessageAction = async (action) => {
  if (!currentMessage) return
  setActionLoading(true)
  try {
    if (action === 'skip') {
      await markMessageAsRead(currentMessage.id)
      toast.info("Notification marked as read")
    } else {
      // action is the actual button label (e.g. "Yes", "Interested", etc.)
      await submitMessageResponse(currentMessage.id, action)
      toast.success("Thankyou! Your response has been captured")
    }

    setNotifications(prev =>
      prev.map(n =>
        n.id === currentMessage.id
          ? { ...n, responded: true, response: action, read: true }
          : n
      )
    )
    setShowMessageModal(false)
    setCurrentMessage(null)
  } catch (error) {
    toast.error('Failed to submit response. Please try again.')
  } finally {
    setActionLoading(false)
  }
}

  return (
    <div className="min-h-screen bg-[#F5F5FF]">
      <div className="pt-20 pb-6">

        <div className="flex items-center gap-1 px-2 py-2">
          <button
            onClick={() => navigate(-1)}
            className="flex items-center justify-center w-9 h-9 text-indigo-700
              hover:bg-indigo-100 rounded-full transition-colors"
          >
            <ArrowLeft className="w-5 h-5" />
          </button>
          <div className="flex items-center gap-2">
            <Bell className="w-4 h-4 text-indigo-500" />
            <h1 className="text-base font-semibold text-gray-800">Notifications</h1>
          </div>
        </div>

        {loading ? (
          <div className="px-4 space-y-3 mt-2">
            {[...Array(4)].map((_, i) => (
              <div key={i} className="bg-white rounded-xl p-4 shadow-sm animate-pulse">
                <div className="flex justify-between mb-2">
                  <div className="h-3.5 bg-gray-200 rounded w-2/5" />
                  <div className="h-3 bg-gray-100 rounded w-14" />
                </div>
                <div className="h-3 bg-gray-100 rounded w-full mb-1.5" />
                <div className="h-3 bg-gray-100 rounded w-3/4" />
              </div>
            ))}
          </div>
        ) : notifications.length === 0 ? (
          <div className="flex flex-col items-center justify-center pt-24 px-6 text-center">
            <div className="w-16 h-16 rounded-full bg-indigo-50 flex items-center justify-center mb-4">
              <BellOff className="w-7 h-7 text-indigo-300" />
            </div>
            <p className="text-gray-500 text-sm">No notifications yet</p>
            <p className="text-gray-400 text-xs mt-1">
              You'll see updates from your employer here.
            </p>
          </div>
        ) : (
          <>
            <p className="text-xs text-gray-400 px-4 mb-3 mt-1">
              Showing last {notifications.length} notification{notifications.length !== 1 ? 's' : ''}
            </p>
            {notifications.map(notif => (
              <NotificationCard
                key={notif.id}
                notification={notif}
                onOpen={handleOpenNotification}
              />
            ))}
          </>
        )}
      </div>

      {showMessageModal && currentMessage && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-2xl w-full max-w-md relative shadow-2xl max-h-[90vh] flex flex-col">
            <div className="p-6 border-b border-gray-200 flex-shrink-0 flex items-center justify-between">
              <h3 className="text-xl font-semibold text-gray-900">Work Request</h3>
              <button
                onClick={() => { setShowMessageModal(false); setCurrentMessage(null); }}
                className="p-1 hover:bg-gray-100 rounded-full transition-colors"
              >
                <X className="h-5 w-5 text-gray-500" />
              </button>
            </div>
            <div className="p-6 overflow-y-auto flex-1">
              <div className="bg-gray-50 rounded-lg p-4 border border-gray-200">
                {currentMessage.title && (
                  <h4 className="text-lg font-semibold text-gray-900 mb-3">
                    {currentMessage.title}
                  </h4>
                )}
                <p className="text-gray-700 leading-relaxed whitespace-pre-wrap break-words">
                  {currentMessage.body}
                </p>
              </div>
            </div>
            <div className="p-6 border-t border-gray-200 flex gap-3 flex-shrink-0">
              <button
                disabled={actionLoading}
                onClick={() => handleMessageAction(currentMessage.button1Label || "Yes")}
                className="flex-1 py-2.5 px-4 bg-green-500 text-white rounded-lg hover:bg-green-600 transition-colors font-medium disabled:opacity-50"
              >
                {currentMessage.button1Label || "Yes"}
              </button>
              <button
                disabled={actionLoading}
                onClick={() => handleMessageAction(currentMessage.button2Label || "No")}
                className="flex-1 py-2.5 px-4 bg-red-500 text-white rounded-lg hover:bg-red-600 transition-colors font-medium disabled:opacity-50"
              >
                {currentMessage.button2Label || "No"}
              </button>
              <button
                disabled={actionLoading}
                onClick={() => handleMessageAction('skip')}
                className="flex-1 py-2.5 px-4 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors font-medium disabled:opacity-50"
              >
                Skip
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

export default NotificationHistoryPage